import { Card } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import { Package, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";

type Product = components["schemas"]["ProductOut"];

/** Read-only product list with Preferred CP (plan v2 /products, M1-1). Product truth is Seed's;
 *  fixture data is marked as such (ADR 0023). */
export function ProductsPage() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["products"], queryFn: () => call<Product[]>(api.GET("/app/v1/products")) });
  return (
    <div className="space-y-8">
      <PageHeader titleKey="products.title" subtitle={t("products.subtitle")} />
      <QueryState query={q} emptyKey="products.empty">
        {(rows) => (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((p) => (
              <Card key={p.id} className="flex items-start gap-4 p-6">
                <span className="inline-flex size-14 shrink-0 items-center justify-center rounded-[var(--radius-card)] bg-[var(--accent-primary-soft)] text-violet">
                  <Package aria-hidden="true" className="size-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-[18px] font-semibold text-ink">{p.name}</h2>
                    {p.is_fixture && (
                      <span className="rounded-full border border-line px-2 py-0.5 text-[11px] font-medium text-ink-2">
                        {t("common.fixture_data")}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[13px] text-ink-2">{p.code}</div>
                  <div className="mt-4 flex items-center gap-2 text-[14px]">
                    <UserRound aria-hidden="true" className="size-4 text-ink-2" />
                    <span className="text-ink-2">{t("products.preferred_cp")}</span>
                    <span className="font-medium text-ink">{p.preferred_cp_name ?? t("products.unassigned")}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}
